import { ValidationFormData } from '@/types/validationFormTypes';
import { validate } from '../data/normalisers';
import { DocumentType, WarningKey } from '@/api/generated-types';
import type { SectionKey } from '@/hooks/workflow/navigation/useSetSection';

export interface ValidationWarning {
  fieldPath: string;
  message: string;
  section: SectionKey;
  ctuId?: string;
}

const TRANSPORT_FIELD_LABELS = {
  portOfLoading: 'port of loading',
  portOfDischarge: 'port of discharge',
  placeOfDelivery: 'place of delivery',
} as const;

const normaliseForComparison = (value?: string | null) =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

interface DocumentFieldRule {
  fieldPath: string;
  section: SectionKey;
  message: string;
  /** Skips the rule outright: the field is absent on this document type, or its precondition
   *  is unmet. Distinct from `isInvalid` so an inapplicable field never reads as a clean one. */
  applies?: (formData: ValidationFormData) => boolean;
  isInvalid: (formData: ValidationFormData) => boolean;
}

const DOCUMENT_RULES: DocumentFieldRule[] = [
  {
    fieldPath: 'shipper.name',
    section: 'parties',
    message: 'Shipper information is empty',
    isInvalid: (f) => validate.isEmpty(f.shipper?.name),
  },
  {
    fieldPath: 'consignee.name',
    section: 'parties',
    message: 'Consignee information is empty',
    isInvalid: (f) => validate.isEmpty(f.consignee?.name),
  },
  {
    fieldPath: 'freightForwarder.name',
    section: 'parties',
    message: 'If freight forwarder is provided, it must not be empty',
    applies: (f) => !!f.freightForwarder?.name,
    isInvalid: (f) => validate.isEmpty(f.freightForwarder?.name),
  },
  {
    fieldPath: 'bookingNumber',
    section: 'references',
    message: 'Booking number is empty',
    isInvalid: (f) => validate.isEmpty(f.bookingNumber),
  },
];

const TRANSPORT_RULES: DocumentFieldRule[] = [
  {
    fieldPath: 'portOfLoading',
    section: 'transport',
    message: 'Port of loading is empty',
    // DCM carries its ports per cargo item, not once per document.
    applies: (f) => f.documentType !== DocumentType.Dcm,
    isInvalid: (f) => validate.isEmpty(f.portOfLoading),
  },
  {
    fieldPath: 'portOfDischarge',
    section: 'transport',
    message: 'Port of discharge is empty',
    applies: (f) => f.documentType !== DocumentType.Dcm,
    isInvalid: (f) => validate.isEmpty(f.portOfDischarge),
  },
  {
    fieldPath: 'vesselName',
    section: 'transport',
    message: 'Vessel name is empty',
    isInvalid: (f) => validate.isEmpty(f.vesselName),
  },
  {
    fieldPath: 'voyageNumber',
    section: 'transport',
    message: 'Voyage number is empty',
    isInvalid: (f) => validate.isEmpty(f.voyageNumber),
  },
];

const CERTIFICATE_RULES: DocumentFieldRule[] = [
  {
    fieldPath: 'dgd.containerPackingCertificate.signed',
    section: 'container',
    message: 'Container packing certificate must be completed and signed',
    isInvalid: (f) => !f.dgd?.containerPackingCertificate?.signed,
  },
  {
    fieldPath: 'dgd.containerPackingCertificate.signatoryName',
    section: 'container',
    message: 'Signatory name is empty, certificate is signed',
    applies: (f) => !!f.dgd?.containerPackingCertificate?.signed,
    isInvalid: (f) => validate.isEmpty(f.dgd?.containerPackingCertificate?.signatoryName),
  },
  {
    fieldPath: 'dgd.declaration.signed',
    section: 'container',
    message: 'Declaration must be signed by shipper or authorized personnel',
    isInvalid: (f) => !f.dgd?.declaration?.signed,
  },
  {
    fieldPath: 'dgd.declaration.signatoryName',
    section: 'container',
    message: 'Signatory name is empty, declaration is signed',
    applies: (f) => !!f.dgd?.declaration?.signed,
    isInvalid: (f) => validate.isEmpty(f.dgd?.declaration?.signatoryName),
  },
];

function collect(
  rules: DocumentFieldRule[],
  formData: ValidationFormData,
  warnings: ValidationWarning[]
): void {
  for (const rule of rules) {
    if (rule.applies && !rule.applies(formData)) continue;
    if (rule.isInvalid(formData)) {
      warnings.push({
        fieldPath: rule.fieldPath,
        message: rule.message,
        section: rule.section,
      });
    }
  }
}

/** Container and seal number, per CTU — one pair of rules per unit, so indexed rather than tabled. */
function collectCtuWarnings(formData: ValidationFormData, warnings: ValidationWarning[]): void {
  formData.cargoTransportUnits?.forEach((ctu, index) => {
    const ctuId = ctu.ctuId ?? `CTU-${index + 1}`;

    if (validate.isEmpty(ctu.containerInformation?.containerNumber)) {
      warnings.push({
        fieldPath: `cargoTransportUnits.${index}.containerInformation.containerNumber`,
        message: 'Container number is empty',
        section: 'dangerousGoods',
        ctuId,
      });
    }

    if (validate.isEmpty(ctu.containerInformation?.sealNumber)) {
      warnings.push({
        fieldPath: `cargoTransportUnits.${index}.containerInformation.sealNumber`,
        message: 'Seal number is empty',
        section: 'dangerousGoods',
        ctuId,
      });
    }
  });
}

/** Reads across fields rather than judging one, so it can't be a rule: the vessel name repeating
 *  a port is the signature of a document whose boxes arrived rotated by one. */
function collectVesselClash(formData: ValidationFormData, warnings: ValidationWarning[]): void {
  const vessel = normaliseForComparison(formData.vesselName);
  if (!vessel) return;

  const clash = (
    [
      ['portOfLoading', formData.portOfLoading],
      ['portOfDischarge', formData.portOfDischarge],
      ['placeOfDelivery', formData.placeOfDelivery],
    ] as const
  ).find(([, value]) => normaliseForComparison(value) === vessel);

  if (clash) {
    warnings.push({
      fieldPath: 'vesselName',
      message: `Vessel name is the same as ${TRANSPORT_FIELD_LABELS[clash[0]]} — check the fields are not shifted`,
      section: 'transport',
    });
  }
}

export function validateDocumentFields(formData: ValidationFormData): ValidationWarning[] {
  const warnings: ValidationWarning[] = [];

  collect(DOCUMENT_RULES, formData, warnings);
  collectCtuWarnings(formData, warnings);
  collect(TRANSPORT_RULES, formData, warnings);
  collectVesselClash(formData, warnings);
  collect(CERTIFICATE_RULES, formData, warnings);

  return warnings;
}

/**
 * Maps a warning's fieldPath to its persisted WarningKey. fieldPath is the react-hook-form path
 * (nested under the dgd/dcm groups since B3, e.g. `dgd.declaration.signed`), but WarningKey — the
 * team-settings disable key — stays flat (`declaration.signed`). Strip the group prefix to match.
 */
export function toWarningKey(fieldPath: string): WarningKey {
  return fieldPath.replace(/^(dgd|dcm)\./, '') as WarningKey;
}

export function filterWarnings(
  warnings: ValidationWarning[],
  disabledWarnings: Set<WarningKey>,
  hiddenSections?: Set<string>
): ValidationWarning[] {
  if (!disabledWarnings.size && !hiddenSections?.size) return warnings;
  return warnings.filter((w) => {
    if (hiddenSections?.has(w.section)) return false;
    const basePath = w.ctuId ? w.fieldPath.split('.').pop()! : toWarningKey(w.fieldPath);
    return !disabledWarnings.has(basePath as WarningKey);
  });
}
